import React, { useState, useEffect } from 'react';
import '../pages/Configuracoes.css';
import api from '../services/api';
import { toast } from 'react-toastify';

const CARGOS = [
  { value: 'dono',        label: 'Dono' },
  { value: 'admin',       label: 'Administrador' },
  { value: 'gestor',      label: 'Gestor' },
  { value: 'financeiro',  label: 'Financeiro' },
  { value: 'funcionario', label: 'Funcionário' },
];

const CARGO_LABEL = CARGOS.reduce((acc, c) => ({ ...acc, [c.value]: c.label }), {});

const EMPTY_USER_FORM = {
  username: '', first_name: '', last_name: '', email: '',
  cargo: 'funcionario', is_active: true, senha: '', empresa: '',
};

function UsuariosTab() {
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  const [users, setUsers]       = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY_USER_FORM);
  const [saving, setSaving]     = useState(false);
  const currentUsername = localStorage.getItem('username');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    fetchUsers();
    if (isSuperuser) fetchEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/usuarios/', { headers: headers() });
      setUsers(res.data);
    } catch {
      toast.error('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const res = await api.get('/empresas/', { headers: headers() });
      setEmpresas(res.data);
    } catch {
      toast.error('Erro ao carregar empresas.');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_USER_FORM);
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      username:   user.username,
      first_name: user.first_name || '',
      last_name:  user.last_name  || '',
      email:      user.email      || '',
      cargo:      user.cargo || 'funcionario',
      is_active:  user.is_active,
      senha:      '',
      empresa:    '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.senha)   delete payload.senha;
      if (!payload.empresa) delete payload.empresa;

      if (editing) {
        const res = await api.patch(`/usuarios/${editing.id}/`, payload, {
          headers: { ...headers(), 'Content-Type': 'application/json' },
        });
        setUsers(users.map((u) => (u.id === editing.id ? res.data : u)));
        toast.success('Usuário atualizado.');
      } else {
        const res = await api.post('/usuarios/', payload, {
          headers: { ...headers(), 'Content-Type': 'application/json' },
        });
        setUsers([res.data, ...users]);
        toast.success('Usuário criado.');
      }
      setShowForm(false);
    } catch (err) {
      if (err._planLimitHandled) return;
      const data = err.response?.data;
      const msg = typeof data === 'object'
        ? Object.values(data).flat().join(' ')
        : 'Erro ao salvar usuário.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Excluir o usuário "${user.username}"?`)) return;
    try {
      await api.delete(`/usuarios/${user.id}/`, { headers: headers() });
      setUsers(users.filter((u) => u.id !== user.id));
      toast.success('Usuário excluído.');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao excluir usuário.');
    }
  };

  const formatDate = (str) =>
    str ? new Date(str).toLocaleDateString('pt-BR') : '—';

  const colCount = isSuperuser ? 8 : 7;

  return (
    <div className="cfg-card">
      <div className="cfg-card-accent" />
      <div className="cfg-card-body">
        <header className="cfg-section-header cfg-section-header--between">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span className="material-symbols-outlined cfg-section-icon">group</span>
            <div>
              <h3 className="cfg-section-title">Gestão de Usuários</h3>
              <p className="cfg-section-sub">Crie, edite ou desative contas de acesso ao sistema.</p>
            </div>
          </div>
          <button className="cfg-btn-primary" onClick={openCreate}>
            <span className="material-symbols-outlined">person_add</span>
            Novo Usuário
          </button>
        </header>

        {/* Inline form */}
        {showForm && (
          <div className="cfg-user-form">
            <h4 className="cfg-user-form-title">
              {editing ? 'Editar Usuário' : 'Novo Usuário'}
            </h4>
            <form onSubmit={handleSave}>
              <div className="cfg-row">
                <div className="cfg-field">
                  <label className="cfg-label">Usuário *</label>
                  <input
                    className={`cfg-input${editing ? ' cfg-input--readonly' : ''}`}
                    type="text"
                    placeholder="nome.usuario"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    required
                    readOnly={!!editing}
                  />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">E-mail</label>
                  <input
                    className="cfg-input"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="cfg-row">
                <div className="cfg-field">
                  <label className="cfg-label">Nome</label>
                  <input
                    className="cfg-input"
                    type="text"
                    placeholder="Primeiro nome"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Sobrenome</label>
                  <input
                    className="cfg-input"
                    type="text"
                    placeholder="Sobrenome"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="cfg-row">
                <div className="cfg-field">
                  <label className="cfg-label">
                    {editing ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                  </label>
                  <input
                    className="cfg-input"
                    type="password"
                    placeholder={editing ? 'Deixe em branco para não alterar' : 'Mínimo 8 caracteres'}
                    minLength={editing ? undefined : 8}
                    value={form.senha}
                    onChange={(e) => setForm({ ...form, senha: e.target.value })}
                    required={!editing}
                  />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Cargo</label>
                  <select
                    className="cfg-input"
                    value={form.cargo}
                    onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                  >
                    {CARGOS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cfg-row">
                {isSuperuser && !editing && (
                  <div className="cfg-field">
                    <label className="cfg-label">Empresa *</label>
                    <select
                      className="cfg-input"
                      value={form.empresa}
                      onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                      required
                    >
                      <option value="">Selecione...</option>
                      {empresas.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="cfg-field cfg-field--checkboxes">
                  <label className="cfg-label">Status</label>
                  <label className="cfg-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                    Conta ativa
                  </label>
                </div>
              </div>

              <div className="cfg-form-actions">
                <button
                  type="button"
                  className="cfg-btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="cfg-btn-primary" disabled={saving}>
                  <span className="material-symbols-outlined">
                    {editing ? 'save' : 'person_add'}
                  </span>
                  {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users table */}
        <div className="cfg-table-wrap">
          <table className="cfg-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Nome</th>
                <th>E-mail</th>
                {isSuperuser && <th>Empresa</th>}
                <th className="center">Cargo</th>
                <th className="center">Status</th>
                <th className="center">Desde</th>
                <th className="right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: colCount }).map((__, j) => (
                      <td key={j}><div className="skeleton" /></td>
                    ))}
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="cfg-empty">Nenhum usuário cadastrado.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="td-name">{user.username}</td>
                    <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>{user.email || '—'}</td>
                    {isSuperuser && <td>{user.empresa_nome || '—'}</td>}
                    <td className="center">
                      <span className={`badge ${user.is_staff ? 'cfg-badge-admin' : 'cfg-badge-op'}`}>
                        {CARGO_LABEL[user.cargo] || 'Operador'}
                      </span>
                    </td>
                    <td className="center">
                      {user.is_active
                        ? <span className="badge badge-ok">Ativo</span>
                        : <span className="badge badge-error">Inativo</span>
                      }
                    </td>
                    <td className="center">{formatDate(user.date_joined)}</td>
                    <td className="right">
                      <div className="td-actions">
                        <button
                          className="prd-icon-btn"
                          onClick={() => openEdit(user)}
                          title="Editar"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        {user.username !== currentUsername && (
                          <button
                            className="prd-icon-btn prd-icon-btn--danger"
                            onClick={() => handleDelete(user)}
                            title="Excluir"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default UsuariosTab;
